import { Injectable } from '@zhfe/vue2-di-plugin'
import { UserService } from '@/services/user.service'
import common from '@/client/modules/common'
import device from '@/client/modules/device'
import {
  callRequest,
  client_feedback,
  client_checkMethod,
  client_getVipVersion,
  client_getTryNo,
  client_openOptionWindow,
  client_check_switch,
  client_check_abtest,
  client_open_vip_box,
  client_openKisProcess,
  client_get_device_info,
  client_refresh_userinfo,
  client_logout,
  client_open_login_window,
  client_dom_loaded
} from '@vipsdk/js-bridge'

enum Interface {
  /** 打开反馈窗口 */
  callClient = 'callClient',
  /** 检查是否存在某个方法 */
  checkMethod = 'checkMethod',
  /** 上报 */
  report = 'report',
  /** 获取版本号(cookie也有) */
  getVipVersion = 'getVipVersion',
  /** 获取渠道号 */
  getTryNo = 'getTryNo',
  /** 登录受限 */
  tokenExpiration = 'tokenExpiration',
  /** 通知客户端打开窗口 */
  openOptionWindow = 'openOptionWindow',
  /** 检查功能开关 { app_name, key_name } */
  isSwitchOn = 'isSwitchOn',
  /** 检查ab测的开关 { uuid } */
  getABTest = 'getABTest',
  /** 打开百宝箱应用 */
  openVipBoxApp = 'openVipBoxApp',
  /** 打开闪电查杀等应用 { path:kismain.exe, cmd } */
  openKisProcess = 'openKisProcess',
  /**  获取设备的信息 系统名和版本 */
  getPCInfo = 'getPCInfo',
  /** 刷新用户信息 */
  refreshUserInfo = 'refreshUserInfo',
  /** 退出登录 */
  exit = 'exit',
  /** 打开登录窗口 */
  userLogin = 'userLogin',
  /** 获取用户是否登录过 */
  isUserLogin = 'isUserLogin',
  /** 登陆成功后，通知客户端刷新页面 */
  winClose = 'winClose',
  /** 刷新页面 */
  refreshPages = 'refreshPages',
  /** sdk统一检查毒霸是否安装 */
  isDubaInstalled = 'isDubaInstalled',
  /** sdk去安装毒霸 */
  installDuba = 'installDuba',
  /** sdk统一打开 */
  openDubaRight = 'openDubaRight',
  /** tid */
  getTid = 'getTid',
  /** 客户端子产品id和产品功能id */
  getScenesInfo = 'getScenesInfo',
  /** 删除用户信息 */
  deleteUser = 'deleteUser',
  /** 关闭窗口 */
  closeWindow = 'closeWindow',
  /** 获取客户端安装时间 */
  GetCurrentProductInstallTime = 'GetCurrentProductInstallTime',
  /** 客户端主版本号 */
  getReaderVersion = 'getReaderVersion',
}

@Injectable()
export class HybridBridgeService {
  constructor (private user: UserService) {
  }

  /** 检查客户端方法 */
  async hasCheckMethod (methodName: string): Promise<1 | 0> {
    const hasMethod = await client_checkMethod(methodName)
    return hasMethod?.result || 0
  }

  async handleMethod (methodName: string, callbackFn: Function) {
    const hasFn = await this.hasCheckMethod(methodName)
    return hasFn && callbackFn()
  }

  /** 页面加载完成 */
  clientDomLoaded () {
    return this.handleMethod(Interface.callClient, () => client_dom_loaded())
  }

  /** 登录 */
  clientUserLogin (params) {
    return this.handleMethod(Interface.userLogin, () => client_open_login_window(params))
  }

  /** 刷新用户 */
  clientRefreshUserinfo () {
    return this.handleMethod(Interface.refreshUserInfo, () => client_refresh_userinfo({ token: this.user.token, open_id: this.user.openId }))
  }

  /** 退出 */
  clientExit () {
    // @ts-ignore
    return this.handleMethod(Interface.exit, () => client_logout())
  }

  /** 打开数据恢复、打开百宝箱 */
  clientOpenVipBox (params) {
    return this.handleMethod(Interface.openVipBoxApp, () => client_open_vip_box(params))
  }

  /** 反馈入口 */
  clientFeedback () {
    return this.handleMethod(Interface.callClient, () => client_feedback())
  }

  /** 版本号 */
  clientVipVersion () {
    return this.handleMethod(Interface.getVipVersion, () => client_getVipVersion())
  }

  /** 获取渠道号 */
  clientGetTryNo () {
    return this.handleMethod(Interface.getTryNo, () => client_getTryNo())
  }

  /** 打开闪电查杀等应用 */
  clientOpenKisProcess (params) {
    return this.handleMethod(Interface.openKisProcess, () => client_openKisProcess(params))
  }

  /** 通知客户端打开窗口 */
  clientOpenOptionWindow (params) {
    return this.handleMethod(Interface.openOptionWindow, () => client_openOptionWindow(params))
  }

  /** 检查功能开关 */
  clientCheckSwitch (params) {
    return this.handleMethod(Interface.isSwitchOn, () => client_check_switch(params))
  }

  /** 检查ab测的开关 { uuid } */
  clientCheckAbtest (params) {
    return this.handleMethod(Interface.getABTest, () => client_check_abtest(params))
  }

  /** sdk统一检查毒霸是否安装 */
  clientHasDubaInstalled () {
    return this.handleMethod(Interface.isDubaInstalled, () => callRequest({ fnName: Interface.isDubaInstalled }))
  }

  /** sdk去安装毒霸 */
  clientInstallDuba () {
    return this.handleMethod(Interface.installDuba, () => callRequest({ fnName: Interface.installDuba }))
  }

  /** sdk统一打开 */
  clientDubaAppCommonOpen () {
    return this.handleMethod(Interface.openDubaRight, () => callRequest({ fnName: Interface.openDubaRight }))
  }

  /** 客户端获取tid */
  clientGetTid () {
    return this.handleMethod(Interface.getTid, () => callRequest({ fnName: Interface.getTid }))
  }

  /** 获取客户端子产品id和产品功能id */
  clientGetScenesInfo () {
    return this.handleMethod(Interface.getScenesInfo, () => callRequest({ fnName: Interface.getScenesInfo }))
  }

  /** 登录--删除用户信息 */
  clientDeleteUser () {
    return this.handleMethod(Interface.deleteUser, () => callRequest({ fnName: Interface.deleteUser }))
  }

  /** 登录--关闭窗口 */
  clientCloseWindow () {
    return this.handleMethod(Interface.closeWindow, () => callRequest({ fnName: Interface.closeWindow }))
  }

  /** 客户端关闭 */
  clientWinclose () {
    return this.handleMethod(Interface.winClose, () => callRequest({ fnName: Interface.winClose }))
  }

  /** 获取客户端安装时间 */
  clientInstallTime () {
    return this.handleMethod(Interface.GetCurrentProductInstallTime, () => callRequest({ fnName: Interface.GetCurrentProductInstallTime }))
  }

  /** 获取设备的信息 系统名和版本 (系统版本号) */
  clientGetDeviceInfo () {
    // @ts-ignore 
    return this.handleMethod(Interface.getPCInfo, () => client_get_device_info())
  }

  /** 获取客户端安装时间 */
  clientMajorVersion () {
    return this.handleMethod(Interface.getReaderVersion, () => callRequest({ fnName: Interface.getReaderVersion }))
  }

  paySuccess () {
    common.API.paySuccess({ token: this.user.token, open_id: this.user.openId })
  }

  reportDeviceAdminOperate () {
    device.REPORT.reportDeviceAdminOperate({})
  }
}
